import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '../types';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (user: User) => void;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // 컴포넌트 마운트 시 localStorage에서 사용자 정보 복구 + 서버 상태 검증
  useEffect(() => {
    const init = async () => {
      const savedUser = localStorage.getItem('user');
      const token = localStorage.getItem('accessToken');

      if (savedUser && token) {
        try {
          const userData = JSON.parse(savedUser);
          setUser(userData);

          // 새로고침 시 서버에서 최신 유저 상태 재확인 (SUSPENDED/BANNED 체크)
          try {
            const res = await apiClient.get<{ result: { userStatus: string } }>(
              API_ENDPOINTS.PROFILE.GET
            );
            const freshStatus = res.result?.userStatus;
            if (freshStatus && freshStatus !== userData.status) {
              const updatedUser = { ...userData, status: freshStatus };
              setUser(updatedUser);
              localStorage.setItem('user', JSON.stringify(updatedUser));
            }
          } catch {
            // 서버 검증 실패 시 localStorage 데이터 유지
          }
        } catch (error) {
          // 잘못된 데이터가 있으면 정리
          localStorage.removeItem('user');
          localStorage.removeItem('accessToken');
        }
      }

      setIsLoading(false);
    };

    init();
  }, []);

  const login = (userData: User) => {
    setUser(userData);
    // login 시에도 localStorage에 사용자 정보 저장 (혹시 누락될 경우 대비)
    localStorage.setItem('user', JSON.stringify(userData));
    console.log('AuthContext login - localStorage에 사용자 정보 저장:', userData);
  };

  const logout = async () => {
    console.log('AuthContext logout - 로그아웃 시작');
    
    // API 클라이언트의 logout 메서드 호출 (서버에서 쿠키 제거 + localStorage 정리)
    try {
      await apiClient.logout();
    } catch (error) {
      console.error('로그아웃 API 호출 실패:', error);
      // API 호출 실패해도 로컬 상태는 정리
      localStorage.removeItem('user');
      localStorage.removeItem('accessToken');
    }
    
    setUser(null);
    console.log('AuthContext logout - 로그아웃 완료');
  };

  const updateUser = (updates: Partial<User>) => {
    if (user) {
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);
      // localStorage도 업데이트
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};